import React, { useState, useEffect } from 'react';
import { Calendar as CalendarIcon, Filter, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import MealScheduleList from '../components/MealScheduleList';
import DatePicker from '../components/DatePicker';
import MealTypeSummary from '../components/MealTypeSummary';
import { Shift, ScheduledMeal } from '../types';
import { getScheduledMeals } from '../lib/queries';

interface DashboardProps {
  currentDate: Date;
  setCurrentDate: React.Dispatch<React.SetStateAction<Date>>;
}

const Dashboard: React.FC<DashboardProps> = ({ currentDate, setCurrentDate }) => {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [selectedShift, setSelectedShift] = useState<Shift | 'all'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [meals, setMeals] = useState<ScheduledMeal[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMeals = async () => {
      try {
        setLoading(true);
        const date = currentDate.toISOString().split('T')[0];
        const fetchedMeals = await getScheduledMeals(date);
        setMeals(fetchedMeals);
      } catch (error) {
        console.error('Error fetching meals:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMeals();
  }, [currentDate]);

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const goToNextDay = () => {
    const nextDay = new Date(currentDate);
    nextDay.setDate(nextDay.getDate() + 1);
    setCurrentDate(nextDay);
  };

  const goToPreviousDay = () => {
    const prevDay = new Date(currentDate);
    prevDay.setDate(prevDay.getDate() - 1);
    setCurrentDate(prevDay);
  };

  const filteredMeals = meals.filter(meal => {
    const shiftMatches = selectedShift === 'all' || meal.shift === selectedShift;
    const searchMatches = 
      meal.mealTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
      meal.kidName.toLowerCase().includes(searchTerm.toLowerCase());
    
    return shiftMatches && searchMatches;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="flex items-center">
          <button 
            onClick={goToPreviousDay}
            className="p-2 rounded-full hover:bg-gray-200 transition-colors"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          
          <div className="relative">
            <button 
              onClick={() => setIsCalendarOpen(!isCalendarOpen)}
              className="flex items-center mx-2 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <CalendarIcon className="h-5 w-5 mr-2 text-emerald-600" />
              <span className="font-medium">{formatDate(currentDate)}</span>
            </button>
            
            {isCalendarOpen && (
              <div className="absolute z-10 mt-1 bg-white rounded-lg shadow-lg">
                <DatePicker 
                  selectedDate={currentDate}
                  onDateSelect={(date) => {
                    setCurrentDate(date);
                    setIsCalendarOpen(false);
                  }}
                />
              </div>
            )}
          </div>
          
          <button 
            onClick={goToNextDay}
            className="p-2 rounded-full hover:bg-gray-200 transition-colors"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-grow">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search meals or kids..."
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="flex items-center">
            <Filter className="h-5 w-5 mr-2 text-gray-500" />
            <div className="flex bg-gray-100 rounded-lg overflow-hidden">
              <button 
                className={`px-4 py-2 text-sm font-medium ${
                  selectedShift === 'all' 
                    ? 'bg-emerald-600 text-white' 
                    : 'text-gray-700 hover:bg-gray-200'
                }`}
                onClick={() => setSelectedShift('all')}
              >
                All
              </button>
              <button 
                className={`px-4 py-2 text-sm font-medium ${
                  selectedShift === 'morning' 
                    ? 'bg-emerald-600 text-white' 
                    : 'text-gray-700 hover:bg-gray-200'
                }`}
                onClick={() => setSelectedShift('morning')}
              >
                Morning
              </button>
              <button 
                className={`px-4 py-2 text-sm font-medium ${
                  selectedShift === 'afternoon' 
                    ? 'bg-emerald-600 text-white' 
                    : 'text-gray-700 hover:bg-gray-200'
                }`}
                onClick={() => setSelectedShift('afternoon')}
              >
                Afternoon
              </button>
            </div>
          </div>
          
          <button className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors">
            Export
          </button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <MealTypeSummary filteredMeals={filteredMeals} />
        
        <div className="lg:col-span-3">
          <MealScheduleList 
            meals={filteredMeals} 
            selectedDate={currentDate}
          />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;